import type { ExperimentName, ExperimentParams } from '@canvas/client-feature-flags';

/**
 * Describes one Databases experiment parameter that should be copied onto
 * Reliability metric metadata as a dashboard-friendly top-level dimension.
 */
export type ReliabilityExperimentDimension<
  ExpName extends ExperimentName = ExperimentName
> = {
  experimentName: ExpName;
  paramName: keyof ExperimentParams[ExpName];
  /**
   * Optional mapper applied to the raw param value before it is emitted as the
   * dashboard dimension. Use this when an experiment exposes a boolean-only
   * param (for example `enabled`) but the dashboard should still split on a
   * `'test'`/`'control'` cohort string consistent with other dimensions.
   */
  deriveValue?: (
    paramValue: ExperimentParams[ExpName][keyof ExperimentParams[ExpName]]
  ) => string | number | boolean;
};

/**
 * Databases-owned registry of experiment parameters that are useful for
 * Reliability dashboards.
 *
 * Add a dimension here when a Databases experiment can affect frontend
 * performance or any Reliability metric. The metric transformer emits each
 * entry as a top-level `exp_<experimentName>` field so dashboards can filter by
 * one specific experiment's cohort/group value.
 *
 * Do not add cosmetic experiments, nested experiment bags, raw experiment
 * state, or opaque snapshot hashes here. Those are not useful for off/on
 * dashboard comparisons and make review harder.
 *
 * Cleanup ritual: when an experiment is fully shipped, removed, or no longer
 * relevant to Reliability dashboards, remove its entry from this registry and
 * clean up the associated dashboard split, docs, and tests in the same change.
 */
export const reliabilityExperimentDimensions = [
  {
    experimentName: 'databasesSelectionsPerformance',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    experimentName: 'databasesKeyboardA11y',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    experimentName: 'databasesSdkLoaderBypass',
    paramName: 'isEnabled',
    deriveValue: (isEnabled) => (isEnabled ? 'test' : 'control')
  },
  {
    experimentName: 'databasesViewContextualMenus',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    experimentName: 'databasesContextualMenusM2',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  },
  {
    experimentName: 'databasesFrontendAiFieldsSummary',
    paramName: 'enabled',
    deriveValue: (enabled) => (enabled ? 'test' : 'control')
  }
] satisfies readonly ReliabilityExperimentDimension[];
